import { React, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import {
	Calendar,
	Card,
	Select,
	Space,
	Tag,
	message,
	List,
	Button,
	Modal,
	Input,
	Layout,
	DatePicker,
	Form,
	Popconfirm,
	Checkbox,
	Tabs,
	Table,
} from "antd";
import {
	LeftOutlined,
	RightOutlined,
	EditOutlined,
	DeleteOutlined,
	LinkOutlined,
	DownloadOutlined,
} from "@ant-design/icons";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import XLSX from "xlsx-js-style";
import { fetchKoreanHolidays } from "../../../../utilities/koreanHolidays";
import { createCalendarHolidayRenders } from "../../calendar/calendarHolidayCellRender";
import {
	parseKoreaDateTime,
	toKoreaApiDateTime,
} from "../../../../utilities/koreaTime";
import AccessedCarNavigation from "../AccessedCarNavigation";
import { copyRentalCarShareUrl } from "./rentalCarShare";
import useIsMobile from "../../../../hooks/useIsMobile";

dayjs.extend(isSameOrAfter);
dayjs.locale("ko");

/** 종일(연속) 예약 끝 시각: 종료일 달력날 23:59:58 */
const allDayEndOnCalendarDay = (day) =>
	day.startOf("day").hour(23).minute(59).second(58).millisecond(0);

const RENT_CAR_SHOW_TIME_HALF_HOUR = {
	format: "HH:mm",
	minuteStep: 30,
};

const RENT_CAR_SHOW_TIME_ALL_DAY_EDIT = {
	format: "HH:mm",
	minuteStep: 1,
};

const snapRentalCarDateTimeToHalfHour = (d) => {
	if (!d?.isValid()) {
		return d;
	}
	const roundedMin = Math.round(d.minute() / 30) * 30;
	if (roundedMin === 60) {
		return d.add(1, "hour").minute(0).second(0).millisecond(0);
	}
	return d.minute(roundedMin).second(0).millisecond(0);
};

const getScheduleSortMs = (schedule) => {
	if (schedule.start_at) {
		return parseKoreaDateTime(schedule.start_at).valueOf();
	}
	return dayjs(schedule.start_date).startOf("day").valueOf();
};

const getScheduleDayStart = (schedule) => {
	if (schedule.start_at) {
		return parseKoreaDateTime(schedule.start_at).startOf("day");
	}
	return dayjs(schedule.start_date).startOf("day");
};

const getScheduleDayEnd = (schedule) => {
	if (schedule.end_at) {
		return parseKoreaDateTime(schedule.end_at).startOf("day");
	}
	return dayjs(schedule.end_date).startOf("day");
};

const isAllDaySchedule = (schedule) => {
	if (!schedule?.start_at || !schedule?.end_at) {
		return false;
	}
	const start = parseKoreaDateTime(schedule.start_at);
	const end = parseKoreaDateTime(schedule.end_at);
	if (!start.isValid() || !end.isValid()) {
		return false;
	}
	if (!end.isSameOrAfter(start, "day")) {
		return false;
	}
	const startOk =
		start.hour() === 0 &&
		start.minute() === 0 &&
		start.second() === 0;
	const endOk =
		end.hour() === 23 &&
		end.minute() === 59 &&
		(end.second() === 58 || end.second() === 59);
	return startOk && endOk;
};

const formatScheduleRangeLabel = (schedule) => {
	if (schedule.start_at && schedule.end_at) {
		if (isAllDaySchedule(schedule)) {
			const s = parseKoreaDateTime(schedule.start_at);
			const e = parseKoreaDateTime(schedule.end_at);
			if (s.isSame(e, "day")) {
				return `${s.format("YYYY-MM-DD")} 종일`;
			}
			return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")} 종일`;
		}
		return `${parseKoreaDateTime(schedule.start_at).format("YYYY-MM-DD HH:mm")} ~ ${parseKoreaDateTime(schedule.end_at).format("YYYY-MM-DD HH:mm")}`;
	}
	return `${dayjs(schedule.start_date).format("YYYY-MM-DD")} ~ ${dayjs(schedule.end_date).format("YYYY-MM-DD")}`;
};

/** 리스트뷰 기간: YYYY-MM-DD HH:mm */
const formatListPeriodLabel = (row) => {
	const parse = (raw) => {
		if (raw == null || raw === "") {
			return null;
		}
		const asKorea = parseKoreaDateTime(raw);
		if (asKorea.isValid()) {
			return asKorea;
		}
		const asDay = dayjs(raw);
		return asDay.isValid() ? asDay : null;
	};
	const start = parse(row.start_at || row.start_date);
	const end = parse(
		row.end_at || row.end_date || row.start_at || row.start_date,
	);
	if (!start) {
		return "-";
	}
	if (!end) {
		return start.format("YYYY-MM-DD HH:mm");
	}
	return `${start.format("YYYY-MM-DD HH:mm")} ~ ${end.format("YYYY-MM-DD HH:mm")}`;
};

/** 월간 캘린더 막대: 대여 내역 옆 괄호 안 시간·기간 표기 */
const formatCalendarBarTimeParen = (schedule) => {
	if (schedule.start_at && schedule.end_at) {
		const start = parseKoreaDateTime(schedule.start_at);
		const end = parseKoreaDateTime(schedule.end_at);
		if (!start.isValid() || !end.isValid()) {
			return "";
		}
		if (isAllDaySchedule(schedule)) {
			return "(종일)";
		}
		if (start.isSame(end, "day")) {
			return `(${start.format("HH:mm")} ~ ${end.format("HH:mm")})`;
		}
		return `(${start.format("M/D HH:mm")} ~ ${end.format("M/D HH:mm")})`;
	}
	const sd = dayjs(schedule.start_date);
	const ed = dayjs(schedule.end_date);
	if (!sd.isValid() || !ed.isValid()) {
		return "";
	}
	if (sd.isSame(ed, "day")) {
		return `(${sd.format("M/D")})`;
	}
	return `(${sd.format("M/D")}~${ed.format("M/D")})`;
};

/** 캘린더 셀 Tag: 4글자 초과 시 이후 생략 */
const truncateCellTagText = (text, maxLen = 4) => {
	const s = text == null ? "" : String(text);
	if (s.length <= maxLen) {
		return s;
	}
	return s.slice(0, maxLen);
};

const { Content } = Layout;
const { Option } = Select;

const SCHEDULE_TYPE_RENTAL = "대여";
const SCHEDULE_TYPE_OPERATION = "운행";
const SCHEDULE_TYPE_TRUCK = "트럭";
const SCHEDULE_KIND_RENTAL = "rental";
const SCHEDULE_KIND_INSPECTION = "inspection";
const SCHEDULE_KIND_REPAIR = "repair";
const OTHER_CAR_TYPE_NAME = "기타";
const API_INSPECTION = "/utils/rental/car/inspection-schedules/";
const API_REPAIR = "/utils/rental/car/repair-schedules/";

const isInspectionCalendarItem = (item) =>
	item?._scheduleKind === SCHEDULE_KIND_INSPECTION;

const isRepairCalendarItem = (item) =>
	item?._scheduleKind === SCHEDULE_KIND_REPAIR;

const getScheduleTypeTagColor = (scheduleType) => {
	if (scheduleType === SCHEDULE_TYPE_OPERATION) {
		return "purple";
	}
	if (scheduleType === SCHEDULE_TYPE_TRUCK) {
		return "geekblue";
	}
	return "cyan";
};

/** 캘린더 셀 조회용: dayjs 파싱을 일정당 1회로 줄이기 */
const attachCalendarMeta = (item) => {
	const dayStart = getScheduleDayStart(item);
	const dayEnd = getScheduleDayEnd(item);
	const endForSort =
		item.end_at != null && item.end_at !== ""
			? parseKoreaDateTime(item.end_at)
			: dayjs(item.end_date).endOf("day");
	return {
		...item,
		_sortMs: getScheduleSortMs(item),
		_endSortMs: endForSort.isValid()
			? endForSort.valueOf()
			: dayEnd.valueOf(),
		_dayStart: dayStart,
		_dayEnd: dayEnd,
		_dayStartKey: dayStart.format("YYYY-MM-DD"),
		_dayEndKey: dayEnd.format("YYYY-MM-DD"),
	};
};

const EMPTY_DAY_ITEMS = Object.freeze([]);
const CALENDAR_CONTENT_BASE_HEIGHT = 120;
const CALENDAR_LANE_ROW_HEIGHT = 26.75;
const CALENDAR_MOBILE_TAG_ROW_HEIGHT = 20;

/** 캘린더 weekStart=0(일요일) 기준 주 시작일 키 */
const getSundayWeekStartKey = (date) => {
	const d = dayjs(date);
	return d.subtract(d.day(), "day").format("YYYY-MM-DD");
};

const compareCalendarItems = (a, b) => {
	const sortDiff = a._sortMs - b._sortMs;
	if (sortDiff !== 0) {
		return sortDiff;
	}
	const endDiff = a._endSortMs - b._endSortMs;
	if (endDiff !== 0) {
		return endDiff;
	}
	return String(a._calendarKey).localeCompare(String(b._calendarKey));
};

const normalizeRentalSchedule = (schedule) =>
	attachCalendarMeta({
		...schedule,
		_scheduleKind: SCHEDULE_KIND_RENTAL,
		_calendarKey: `rental-${schedule.id}`,
	});

const normalizeInspectionSchedule = (inspection) =>
	attachCalendarMeta({
		...inspection,
		_scheduleKind: SCHEDULE_KIND_INSPECTION,
		_calendarKey: `inspection-${inspection.id}`,
		start_date: inspection.available_from,
		end_date: inspection.available_to,
	});

const normalizeRepairSchedule = (repair) =>
	attachCalendarMeta({
		...repair,
		_scheduleKind: SCHEDULE_KIND_REPAIR,
		_calendarKey: `repair-${repair.id}`,
		start_date: repair.repair_start_date,
		end_date: repair.repair_end_date,
	});

/** Form.useWatch가 캘린더 전체를 리렌더하지 않도록 필드만 분리 */
const AllDayAwareDatePicker = ({ form, ...datePickerProps }) => {
	const allDay = Form.useWatch("all_day", form);
	return (
		<DatePicker
			{...datePickerProps}
			showTime={
				allDay === true
					? RENT_CAR_SHOW_TIME_ALL_DAY_EDIT
					: RENT_CAR_SHOW_TIME_HALF_HOUR
			}
		/>
	);
};

const driverPayload = (values) => {
	const choice = values.driver_choice;
	if (choice && choice !== "other") {
		return { driver_user: choice, driver: "" };
	}
	if (choice === "other") {
		return {
			driver_user: null,
			driver: values.driver_text?.trim() || "",
		};
	}
	return { driver_user: null, driver: "" };
};

const DriverFields = ({ drivers }) => {
	const choice = Form.useWatch("driver_choice");
	return (
		<>
			<Form.Item name="driver_choice" label="운전자">
				<Select
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="운전 가능 교직원 또는 기타"
					options={[
						...drivers.map((driver) => ({
							value: driver.id,
							label: driver.name,
						})),
						{ value: "other", label: "기타" },
					]}
				/>
			</Form.Item>
			{choice === "other" ? (
				<Form.Item
					name="driver_text"
					label="운전자 이름"
					rules={[
						{
							required: true,
							message: "운전자 이름을 입력해주세요",
						},
					]}>
					<Input placeholder="운전자 이름을 입력하세요" />
				</Form.Item>
			) : null}
		</>
	);
};

const ScheduleCarTypeFields = ({ form, carTypes, allCarTypes }) => {
	const scheduleType = Form.useWatch("schedule_type", form);
	const carTypeId = Form.useWatch("car_type", form);

	const otherCarType = useMemo(
		() =>
			allCarTypes.find(
				(type) => type.name === OTHER_CAR_TYPE_NAME,
			),
		[allCarTypes],
	);

	const carTypeOptions = useMemo(() => {
		if (scheduleType !== SCHEDULE_TYPE_TRUCK || !otherCarType) {
			return carTypes;
		}
		if (carTypes.some((type) => type.id === otherCarType.id)) {
			return carTypes;
		}
		return [...carTypes, otherCarType];
	}, [scheduleType, carTypes, otherCarType]);

	const showCustomCarNumber =
		carTypeId != null &&
		otherCarType != null &&
		Number(carTypeId) === Number(otherCarType.id);

	return (
		<>
			<Form.Item
				name="car_type"
				label="차량 종류"
				rules={[
					{
						required: true,
						message: "차량 종류를 선택해주세요",
					},
				]}>
				<Select placeholder="차량 종류를 선택하세요">
					{carTypeOptions.map((type) => (
						<Option
							key={type.id}
							value={type.id}>
							{type.name}
						</Option>
					))}
				</Select>
			</Form.Item>
			{showCustomCarNumber ? (
				<Form.Item
					name="custom_car_number"
					label="차량 번호"
					rules={[
						{
							required: true,
							message: "차량 번호를 입력하세요.",
						},
					]}>
					<Input placeholder="차량 번호를 입력하세요" />
				</Form.Item>
			) : null}
		</>
	);
};

const ViewRentalCarSchedule = ({ mode = "rental" }) => {
	const isMaintenanceMode = mode === "maintenance";
	const isRentalMode = !isMaintenanceMode;
	const location = useLocation();
	const navigate = useNavigate();
	const [schedules, setSchedules] = useState([]);
	const [inspectionSchedules, setInspectionSchedules] = useState([]);
	const [repairSchedules, setRepairSchedules] = useState([]);
	const [loading, setLoading] = useState(true);
	const [carTypes, setCarTypes] = useState([]);
	const [drivers, setDrivers] = useState([]);
	const [allCarTypes, setAllCarTypes] = useState([]);
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const isMobile = useIsMobile();
	const [modalVisible, setModalVisible] = useState(false);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [inspectionModalVisible, setInspectionModalVisible] =
		useState(false);
	const [repairModalVisible, setRepairModalVisible] = useState(false);
	const [completeModalVisible, setCompleteModalVisible] = useState(false);
	const [completingInspection, setCompletingInspection] = useState(null);
	const [editingSchedule, setEditingSchedule] = useState(null);
	const [editingInspection, setEditingInspection] = useState(null);
	const [editingRepair, setEditingRepair] = useState(null);
	const [form] = Form.useForm();
	const [editForm] = Form.useForm();
	const [inspectionForm] = Form.useForm();
	const [repairForm] = Form.useForm();
	const [completeForm] = Form.useForm();
	const [calendarPanelDate, setCalendarPanelDate] = useState(() =>
		dayjs(),
	);
	const [holidayMap, setHolidayMap] = useState({});
	const [showRentalSchedules, setShowRentalSchedules] = useState(true);
	const [showOperationSchedules, setShowOperationSchedules] =
		useState(true);
	const [viewMode, setViewMode] = useState("calendar");
	const [listRange, setListRange] = useState(() => [
		dayjs().startOf("month"),
		dayjs().endOf("month"),
	]);
	const [listSearch, setListSearch] = useState("");
	const [listScheduleTypeFilters, setListScheduleTypeFilters] =
		useState([]);

	const otherCarTypeId = useMemo(
		() =>
			allCarTypes.find(
				(type) => type.name === OTHER_CAR_TYPE_NAME,
			)?.id,
		[allCarTypes],
	);

	const isOtherCarTypeId = useCallback(
		(carTypeId) =>
			carTypeId != null &&
			Number(carTypeId) === Number(otherCarTypeId),
		[otherCarTypeId],
	);

	const scheduleCarLabel = useCallback((item) => {
		const custom = (item.custom_car_number || "").trim();
		if (custom) {
			return custom;
		}
		if (item.car_type && typeof item.car_type === "object") {
			return item.car_type.name || "-";
		}
		return item.car_type_name || "-";
	}, []);

	const listViewRows = useMemo(() => {
		const rangeStart = listRange?.[0]?.startOf("day");
		const rangeEnd = listRange?.[1]?.endOf("day");
		const overlapsRange = (start, end) => {
			if (!rangeStart || !rangeEnd) {
				return true;
			}
			const s = dayjs(start).startOf("day");
			const e = dayjs(end || start).endOf("day");
			return (
				s.isValid() &&
				e.isValid() &&
				!s.isAfter(rangeEnd, "day") &&
				!e.isBefore(rangeStart, "day")
			);
		};
		const keyword = listSearch.trim().toLowerCase();
		if (isMaintenanceMode) {
			const items = [
				...inspectionSchedules.map((row) => ({
					...normalizeInspectionSchedule(row),
					_listKind: "검사",
				})),
				...repairSchedules.map((row) => ({
					...normalizeRepairSchedule(row),
					_listKind: "수리",
				})),
			].filter((row) =>
				overlapsRange(row.start_date, row.end_date),
			);
			if (!keyword) {
				return items;
			}
			return items.filter((row) => {
				const car = [
					scheduleCarLabel(row),
					row.car_type?.name,
					row.custom_car_number,
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				const assignee = (
					row.assignee || ""
				).toLowerCase();
				return (
					car.includes(keyword) ||
					assignee.includes(keyword)
				);
			});
		}
		const items = schedules
			.filter((schedule) => {
				const type =
					schedule.schedule_type ||
					SCHEDULE_TYPE_RENTAL;
				return (
					type === SCHEDULE_TYPE_RENTAL ||
					type === SCHEDULE_TYPE_OPERATION ||
					type === SCHEDULE_TYPE_TRUCK
				);
			})
			.filter((schedule) =>
				overlapsRange(
					schedule.start_date ||
						schedule.start_at,
					schedule.end_date || schedule.end_at,
				),
			);
		if (!keyword) {
			return items;
		}
		return items.filter((schedule) => {
			const car = [
				scheduleCarLabel(schedule),
				schedule.car_type?.name,
				schedule.custom_car_number,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			const driver = (schedule.driver || "").toLowerCase();
			const applicant = (
				schedule.applicant_name || ""
			).toLowerCase();
			return (
				car.includes(keyword) ||
				driver.includes(keyword) ||
				applicant.includes(keyword)
			);
		});
	}, [
		isMaintenanceMode,
		listRange,
		listSearch,
		schedules,
		inspectionSchedules,
		repairSchedules,
		scheduleCarLabel,
	]);

	const filteredListViewRows = useMemo(() => {
		if (isMaintenanceMode || !listScheduleTypeFilters.length) {
			return listViewRows;
		}
		return listViewRows.filter((row) =>
			listScheduleTypeFilters.includes(
				row.schedule_type || SCHEDULE_TYPE_RENTAL,
			),
		);
	}, [isMaintenanceMode, listScheduleTypeFilters, listViewRows]);

	const visibleCalendarItems = useMemo(() => {
		if (isMaintenanceMode) {
			return [
				...inspectionSchedules.map(
					normalizeInspectionSchedule,
				),
				...repairSchedules.map(normalizeRepairSchedule),
			];
		}
		const rentalItems = schedules
			.filter((schedule) => {
				const type =
					schedule.schedule_type ||
					SCHEDULE_TYPE_RENTAL;
				if (type === SCHEDULE_TYPE_RENTAL) {
					return showRentalSchedules;
				}
				if (type === SCHEDULE_TYPE_OPERATION) {
					return showOperationSchedules;
				}
				return false;
			})
			.map(normalizeRentalSchedule);
		return rentalItems;
	}, [
		isMaintenanceMode,
		schedules,
		inspectionSchedules,
		repairSchedules,
		showRentalSchedules,
		showOperationSchedules,
	]);

	useEffect(() => {
		getAllCarTypes();
		getDrivers();
		if (isRentalMode) {
			getCarTypes();
			getSchedules();
		} else {
			(async () => {
				setLoading(true);
				try {
					await Promise.all([
						(async () => {
							const response =
								await useAxios.get(
									API_INSPECTION,
								);
							if (
								response.status ===
								200
							) {
								setInspectionSchedules(
									response
										.data
										.inspection_schedules ||
										[],
								);
							}
						})(),
						(async () => {
							const response =
								await useAxios.get(
									API_REPAIR,
								);
							if (
								response.status ===
								200
							) {
								setRepairSchedules(
									response
										.data
										.repair_schedules ||
										[],
								);
							}
						})(),
					]);
				} catch (error) {
					message.error(
						"검사·수리 일정을 불러오는데 실패했습니다.",
					);
					console.error(error);
				} finally {
					setLoading(false);
				}
			})();
		}
	}, [isRentalMode]);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const scheduleId = searchParams.get("schedule");
		if (!scheduleId || loading || schedules.length === 0) {
			return;
		}

		const schedule = schedules.find(
			(item) => String(item.id) === scheduleId,
		);
		if (!schedule) {
			return;
		}

		const dayStart = getScheduleDayStart(schedule);
		setSelectedDate(dayStart);
		setCalendarPanelDate(dayStart);
		setModalVisible(true);

		searchParams.delete("schedule");
		const newSearch = searchParams.toString();
		navigate(
			{
				pathname: location.pathname,
				search: newSearch ? `?${newSearch}` : "",
			},
			{ replace: true },
		);
	}, [location.search, location.pathname, loading, schedules, navigate]);

	const getDrivers = async () => {
		try {
			const response = await useAxios.get(
				"/utils/rental/car/drivers/",
			);
			setDrivers(response.data || []);
		} catch {
			setDrivers([]);
		}
	};

	const getCarTypes = async () => {
		try {
			const response = await useAxios.get(
				"/utils/rental/car/types/",
				{ params: { for_rental: 1 } },
			);
			if (response.status === 200) {
				setCarTypes(response.data.car_types);
			}
		} catch (error) {
			message.error("차량 종류를 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const getAllCarTypes = async () => {
		try {
			const response = await useAxios.get(
				"/utils/rental/car/types/",
			);
			if (response.status === 200) {
				setAllCarTypes(response.data.car_types);
			}
		} catch (error) {
			message.error("차량 종류를 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const getSchedules = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/rental/car/schedule/",
			);
			if (response.status === 200) {
				let schedules =
					response.data.rental_car_schedule;
				schedules.sort((a, b) => {
					const diff =
						getScheduleSortMs(a) -
						getScheduleSortMs(b);
					if (diff !== 0) {
						return diff > 0 ? 1 : -1;
					}
					return Number(a.id) - Number(b.id);
				});
				setSchedules(schedules);
			}
		} catch (error) {
			message.error("일정을 불러오는데 실패했습니다.");
			console.error("일정을 불러오는데 실패했습니다.", error);
		} finally {
			setLoading(false);
		}
	};

	const getInspectionSchedules = async () => {
		try {
			const response = await useAxios.get(API_INSPECTION);
			if (response.status === 200) {
				setInspectionSchedules(
					response.data.inspection_schedules ||
						[],
				);
			}
		} catch (error) {
			message.error("검사 일정을 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const getRepairSchedules = async () => {
		try {
			const response = await useAxios.get(API_REPAIR);
			if (response.status === 200) {
				setRepairSchedules(
					response.data.repair_schedules || [],
				);
			}
		} catch (error) {
			message.error("수리 일정을 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const calendarItemsByDay = useMemo(() => {
		const map = new Map();
		const sorted = [...visibleCalendarItems].sort(
			compareCalendarItems,
		);

		for (const item of sorted) {
			let cursor = dayjs(item._dayStartKey);
			const end = dayjs(item._dayEndKey);
			if (!cursor.isValid() || !end.isValid()) {
				continue;
			}
			// 비정상적으로 긴 구간 방어 (렌더 폭주 방지)
			const maxDays = 370;
			let count = 0;
			while (
				cursor.isSameOrBefore(end, "day") &&
				count < maxDays
			) {
				const key = cursor.format("YYYY-MM-DD");
				const list = map.get(key);
				if (list) {
					list.push(item);
				} else {
					map.set(key, [item]);
				}
				cursor = cursor.add(1, "day");
				count += 1;
			}
		}
		return map;
	}, [visibleCalendarItems]);

	const getDayCalendarItems = useCallback(
		(date) => {
			return (
				calendarItemsByDay.get(
					date.format("YYYY-MM-DD"),
				) || EMPTY_DAY_ITEMS
			);
		},
		[calendarItemsByDay],
	);

	const getDaySchedules = useCallback(
		(date) => {
			return getDayCalendarItems(date).filter(
				(item) =>
					!isInspectionCalendarItem(item) &&
					!isRepairCalendarItem(item),
			);
		},
		[getDayCalendarItems],
	);

	const getDayInspectionSchedules = useCallback(
		(date) => {
			return getDayCalendarItems(date).filter(
				isInspectionCalendarItem,
			);
		},
		[getDayCalendarItems],
	);

	const getDayRepairSchedules = useCallback(
		(date) => {
			return getDayCalendarItems(date).filter(
				isRepairCalendarItem,
			);
		},
		[getDayCalendarItems],
	);

	const handleDateSelect = (date) => {
		setSelectedDate(date);
		setModalVisible(true);
	};

	const handleAddSchedule = () => {
		form.resetFields();
		form.setFieldsValue({
			start_date: selectedDate.hour(9).minute(0).second(0),
			end_date: selectedDate.hour(18).minute(0).second(0),
			all_day: false,
			schedule_type: SCHEDULE_TYPE_RENTAL,
		});
		setAddModalVisible(true);
	};

	const handleAddInspectionSchedule = () => {
		setEditingInspection(null);
		inspectionForm.resetFields();
		inspectionForm.setFieldsValue({
			inspection_date: selectedDate,
			inspection_due_date: selectedDate,
		});
		setInspectionModalVisible(true);
	};

	const handleEditInspectionSchedule = (inspection) => {
		setEditingInspection(inspection);
		const manualCarLabel =
			inspection.custom_car_number?.trim() ||
			getCarTypeName(inspection.car_type);
		inspectionForm.setFieldsValue({
			custom_car_number: manualCarLabel,
			inspection_date: dayjs(inspection.available_from),
			inspection_due_date: dayjs(
				inspection.inspection_due_date,
			),
			assignee: inspection.assignee || "",
		});
		setInspectionModalVisible(true);
	};

	const handleOpenCompleteInspection = (inspection) => {
		setCompletingInspection(inspection);
		completeForm.setFieldsValue({
			completed_date: inspection.completed_at
				? dayjs(inspection.completed_at)
				: dayjs(),
		});
		setCompleteModalVisible(true);
	};

	const handleCompleteInspectionSubmit = async (values) => {
		if (!completingInspection) {
			return;
		}
		if (!values.completed_date) {
			message.error("검사 완료 날짜를 선택하세요.");
			return;
		}
		try {
			setLoading(true);
			const response = await useAxios.put(
				`${API_INSPECTION}${completingInspection.id}/`,
				{
					is_completed: true,
					completed_date:
						values.completed_date.format(
							"YYYY-MM-DD",
						),
				},
			);
			if (response.status === 200) {
				message.success("검사 완료로 표시했습니다.");
				setCompleteModalVisible(false);
				setCompletingInspection(null);
				completeForm.resetFields();
				getInspectionSchedules();
			}
		} catch (error) {
			message.error("검사 완료 상태 변경에 실패했습니다.");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleInspectionSubmit = async (values) => {
		if (!values.inspection_date) {
			message.error("검사 기한을 선택하세요.");
			return;
		}
		if (!values.inspection_due_date) {
			message.error("검사 마감일을 선택하세요.");
			return;
		}

		const inspectionDate =
			values.inspection_date.format("YYYY-MM-DD");
		const dueDate = values.inspection_due_date.format("YYYY-MM-DD");
		const payload = {
			car_type: otherCarTypeId,
			custom_car_number:
				values.custom_car_number?.trim() || "",
			available_from: inspectionDate,
			available_to: inspectionDate,
			inspection_due_date: dueDate,
			assignee: values.assignee?.trim() || "",
		};

		if (!payload.custom_car_number) {
			message.error("차량 번호를 입력하세요.");
			return;
		}
		if (!payload.car_type) {
			message.error(
				"차량 종류 정보를 불러오지 못했습니다. 잠시 후 다시 시도하세요.",
			);
			return;
		}
		try {
			setLoading(true);
			if (editingInspection) {
				const response = await useAxios.put(
					`${API_INSPECTION}${editingInspection.id}/`,
					payload,
				);
				if (response.status === 200) {
					message.success(
						"검사 일정을 수정했습니다.",
					);
					setInspectionModalVisible(false);
					setEditingInspection(null);
					inspectionForm.resetFields();
					getInspectionSchedules();
				}
			} else {
				const response = await useAxios.post(
					API_INSPECTION,
					payload,
				);
				if (response.status === 201) {
					message.success(
						"검사 일정을 등록했습니다.",
					);
					setInspectionModalVisible(false);
					inspectionForm.resetFields();
					getInspectionSchedules();
				}
			}
		} catch (error) {
			message.error(
				error.response?.data?.message ||
					error.response?.data?.error
						?.custom_car_number?.[0] ||
					(editingInspection
						? "검사 일정 수정에 실패했습니다."
						: "검사 일정 등록에 실패했습니다."),
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteInspectionSchedule = async (inspectionId) => {
		try {
			setLoading(true);
			const response = await useAxios.delete(
				`${API_INSPECTION}${inspectionId}/`,
			);
			if (response.status === 200) {
				message.success("검사 일정을 삭제했습니다.");
				getInspectionSchedules();
			}
		} catch (error) {
			message.error("검사 일정 삭제에 실패했습니다.");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleAddRepairSchedule = () => {
		setEditingRepair(null);
		repairForm.resetFields();
		repairForm.setFieldsValue({
			repair_start_date: selectedDate,
			repair_end_date: selectedDate,
		});
		setRepairModalVisible(true);
	};

	const handleEditRepairSchedule = (repair) => {
		setEditingRepair(repair);
		const manualCarLabel =
			repair.custom_car_number?.trim() ||
			getCarTypeName(repair.car_type);
		repairForm.setFieldsValue({
			custom_car_number: manualCarLabel,
			repair_start_date: dayjs(repair.repair_start_date),
			repair_end_date: dayjs(repair.repair_end_date),
			assignee: repair.assignee || "",
		});
		setRepairModalVisible(true);
	};

	const handleRepairSubmit = async (values) => {
		if (!values.repair_start_date) {
			message.error("수리 시작일을 선택하세요.");
			return;
		}
		if (!values.repair_end_date) {
			message.error("수리 종료일을 선택하세요.");
			return;
		}

		const startDate = values.repair_start_date.format("YYYY-MM-DD");
		const endDate = values.repair_end_date.format("YYYY-MM-DD");
		if (
			values.repair_end_date.isBefore(
				values.repair_start_date,
				"day",
			)
		) {
			message.error(
				"수리 종료일은 시작일과 같거나 이후여야 합니다.",
			);
			return;
		}

		const payload = {
			car_type: otherCarTypeId,
			custom_car_number:
				values.custom_car_number?.trim() || "",
			repair_start_date: startDate,
			repair_end_date: endDate,
			assignee: values.assignee?.trim() || "",
		};

		if (!payload.custom_car_number) {
			message.error("차량 번호를 입력하세요.");
			return;
		}
		if (!payload.car_type) {
			message.error(
				"차량 종류 정보를 불러오지 못했습니다. 잠시 후 다시 시도하세요.",
			);
			return;
		}
		try {
			setLoading(true);
			if (editingRepair) {
				const response = await useAxios.put(
					`${API_REPAIR}${editingRepair.id}/`,
					payload,
				);
				if (response.status === 200) {
					message.success(
						"수리 일정을 수정했습니다.",
					);
					setRepairModalVisible(false);
					setEditingRepair(null);
					repairForm.resetFields();
					getRepairSchedules();
				}
			} else {
				const response = await useAxios.post(
					API_REPAIR,
					payload,
				);
				if (response.status === 201) {
					message.success(
						"수리 일정을 등록했습니다.",
					);
					setRepairModalVisible(false);
					repairForm.resetFields();
					getRepairSchedules();
				}
			}
		} catch (error) {
			message.error(
				error.response?.data?.message ||
					error.response?.data?.error
						?.custom_car_number?.[0] ||
					error.response?.data?.error
						?.repair_end_date?.[0] ||
					(editingRepair
						? "수리 일정 수정에 실패했습니다."
						: "수리 일정 등록에 실패했습니다."),
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteRepairSchedule = async (repairId) => {
		try {
			setLoading(true);
			const response = await useAxios.delete(
				`${API_REPAIR}${repairId}/`,
			);
			if (response.status === 200) {
				message.success("수리 일정을 삭제했습니다.");
				getRepairSchedules();
			}
		} catch (error) {
			message.error("수리 일정 삭제에 실패했습니다.");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleAddSubmit = async (values) => {
		let startDate;
		let endDate;
		if (values.all_day) {
			startDate = values.start_date.startOf("day");
			const endDay = values.end_date.startOf("day");
			if (endDay.isBefore(startDate, "day")) {
				message.error(
					"종일 예약의 종료일은 시작일과 같거나 이후여야 합니다.",
				);
				return;
			}
			endDate = allDayEndOnCalendarDay(endDay);
		} else {
			startDate = snapRentalCarDateTimeToHalfHour(
				values.start_date,
			);
			endDate = snapRentalCarDateTimeToHalfHour(
				values.end_date,
			);
		}

		if (!endDate.isAfter(startDate)) {
			message.error(
				"종료 일시는 시작 일시보다 이후여야 합니다.",
			);
			return;
		}

		try {
			setLoading(true);
			const response = await useAxios.post(
				"/utils/rental/car/schedule/",
				{
					start_date: startDate.format(
						"YYYY-MM-DD",
					),
					end_date: endDate.format("YYYY-MM-DD"),
					start_at: toKoreaApiDateTime(startDate),
					end_at: toKoreaApiDateTime(endDate),
					title: values.title,
					...driverPayload(values),
					applicant_name:
						values.applicant_name?.trim() ||
						"",
					applicant_contact:
						values.applicant_contact?.trim() ||
						"",
					schedule_type:
						values.schedule_type ||
						SCHEDULE_TYPE_RENTAL,
					car_type: values.car_type,
					custom_car_number: isOtherCarTypeId(
						values.car_type,
					)
						? values.custom_car_number?.trim() ||
							""
						: "",
				},
			);

			if (response.status === 201) {
				message.success("차량 예약이 추가되었습니다.");
				setAddModalVisible(false);
				form.resetFields();
				getSchedules();
			}
		} catch (error) {
			const apiError = error.response?.data?.error;
			message.error(
				(typeof apiError === "string" && apiError) ||
					apiError?.custom_car_number?.[0] ||
					"예약 추가 중 오류가 발생했습니다.",
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleEditSchedule = (schedule) => {
		setEditingSchedule(schedule);
		const startVal = schedule.start_at
			? parseKoreaDateTime(schedule.start_at)
			: dayjs(schedule.start_date)
					.hour(9)
					.minute(0)
					.second(0);
		const endVal = schedule.end_at
			? parseKoreaDateTime(schedule.end_at)
			: dayjs(schedule.end_date).hour(18).minute(0).second(0);
		editForm.setFieldsValue({
			start_date: startVal,
			end_date: endVal,
			title: schedule.title,
			driver_choice: schedule.driver_user
				? schedule.driver_user
				: schedule.driver
					? "other"
					: undefined,
			driver_text: schedule.driver_user
				? ""
				: schedule.driver || "",
			applicant_name: schedule.applicant_name || "",
			applicant_contact: schedule.applicant_contact || "",
			schedule_type:
				schedule.schedule_type || SCHEDULE_TYPE_RENTAL,
			car_type: schedule.car_type?.id || schedule.car_type,
			custom_car_number: schedule.custom_car_number || "",
			all_day: isAllDaySchedule(schedule),
		});
		setEditModalVisible(true);
	};

	const handleEditSubmit = async (values) => {
		let startDate;
		let endDate;
		if (values.all_day) {
			startDate = values.start_date.startOf("day");
			const endDay = values.end_date.startOf("day");
			if (endDay.isBefore(startDate, "day")) {
				message.error(
					"종일 예약의 종료일은 시작일과 같거나 이후여야 합니다.",
				);
				return;
			}
			endDate = allDayEndOnCalendarDay(endDay);
		} else {
			startDate = snapRentalCarDateTimeToHalfHour(
				values.start_date,
			);
			endDate = snapRentalCarDateTimeToHalfHour(
				values.end_date,
			);
		}

		if (!endDate.isAfter(startDate)) {
			message.error(
				"종료 일시는 시작 일시보다 이후여야 합니다.",
			);
			return;
		}

		try {
			setLoading(true);
			const response = await useAxios.put(
				`/utils/rental/car/schedule/${editingSchedule.id}/`,
				{
					start_date: startDate.format(
						"YYYY-MM-DD",
					),
					end_date: endDate.format("YYYY-MM-DD"),
					start_at: toKoreaApiDateTime(startDate),
					end_at: toKoreaApiDateTime(endDate),
					title: values.title,
					...driverPayload(values),
					applicant_name:
						values.applicant_name?.trim() ||
						"",
					applicant_contact:
						values.applicant_contact?.trim() ||
						"",
					schedule_type:
						values.schedule_type ||
						SCHEDULE_TYPE_RENTAL,
					car_type: values.car_type,
					custom_car_number: isOtherCarTypeId(
						values.car_type,
					)
						? values.custom_car_number?.trim() ||
							""
						: "",
				},
			);

			if (response.status === 200) {
				message.success("차량 예약이 수정되었습니다.");
				setEditModalVisible(false);
				setEditingSchedule(null);
				editForm.resetFields();
				getSchedules();
			}
		} catch (error) {
			const apiError = error.response?.data?.error;
			message.error(
				(typeof apiError === "string" && apiError) ||
					apiError?.custom_car_number?.[0] ||
					"예약 수정 중 오류가 발생했습니다.",
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleCopyShareLink = async (schedule) => {
		if (!schedule?.share_token) {
			message.error("공유 링크를 생성할 수 없습니다.");
			return;
		}
		try {
			await copyRentalCarShareUrl(schedule.share_token);
			message.success("공유 링크가 복사되었습니다.");
		} catch (error) {
			message.error(
				error.message || "링크 복사에 실패했습니다.",
			);
		}
	};

	const handleDeleteSchedule = async (scheduleId) => {
		try {
			setLoading(true);
			const response = await useAxios.delete(
				`/utils/rental/car/schedule/${scheduleId}/`,
			);

			if (response.status === 200) {
				message.success("차량 예약이 삭제되었습니다.");
				getSchedules();
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"예약 삭제 중 오류가 발생했습니다.",
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const syncAddFormAllDay = useCallback(
		(changed, all) => {
			if ("all_day" in changed) {
				if (changed.all_day === true) {
					const startDay = (
						all.start_date || selectedDate
					).startOf("day");
					let endDay = (
						all.end_date ||
						all.start_date ||
						selectedDate
					).startOf("day");
					if (endDay.isBefore(startDay, "day")) {
						endDay = startDay;
					}
					form.setFieldsValue({
						start_date: startDay,
						end_date: allDayEndOnCalendarDay(
							endDay,
						),
					});
				} else if (changed.all_day === false) {
					const s = (
						all.start_date || selectedDate
					).clone();
					const e = (all.end_date || s).clone();
					form.setFieldsValue({
						start_date: s
							.hour(9)
							.minute(0)
							.second(0),
						end_date: e
							.hour(18)
							.minute(0)
							.second(0),
					});
				}
				return;
			}
			if (all.all_day && changed.start_date) {
				const d = changed.start_date.startOf("day");
				let endDay = all.end_date
					? all.end_date.startOf("day")
					: d;
				if (endDay.isBefore(d, "day")) {
					endDay = d;
				}
				form.setFieldsValue({
					start_date: d,
					end_date: allDayEndOnCalendarDay(
						endDay,
					),
				});
			}
			if (
				all.all_day &&
				changed.end_date &&
				!changed.start_date
			) {
				const endDay = changed.end_date.startOf("day");
				const startDay = all.start_date.startOf("day");
				const e = endDay.isBefore(startDay, "day")
					? startDay
					: endDay;
				form.setFieldsValue({
					start_date: startDay,
					end_date: allDayEndOnCalendarDay(e),
				});
			}
		},
		[form, selectedDate],
	);

	const syncEditFormAllDay = useCallback(
		(changed, all) => {
			if ("all_day" in changed) {
				if (changed.all_day === true) {
					const startDay = (
						all.start_date || dayjs()
					).startOf("day");
					let endDay = (
						all.end_date ||
						all.start_date ||
						dayjs()
					).startOf("day");
					if (endDay.isBefore(startDay, "day")) {
						endDay = startDay;
					}
					editForm.setFieldsValue({
						start_date: startDay,
						end_date: allDayEndOnCalendarDay(
							endDay,
						),
					});
				} else if (changed.all_day === false) {
					const s = (
						all.start_date || dayjs()
					).clone();
					const e = (all.end_date || s).clone();
					editForm.setFieldsValue({
						start_date: s
							.hour(9)
							.minute(0)
							.second(0),
						end_date: e
							.hour(18)
							.minute(0)
							.second(0),
					});
				}
				return;
			}
			if (all.all_day && changed.start_date) {
				const d = changed.start_date.startOf("day");
				let endDay = all.end_date
					? all.end_date.startOf("day")
					: d;
				if (endDay.isBefore(d, "day")) {
					endDay = d;
				}
				editForm.setFieldsValue({
					start_date: d,
					end_date: allDayEndOnCalendarDay(
						endDay,
					),
				});
			}
			if (
				all.all_day &&
				changed.end_date &&
				!changed.start_date
			) {
				const endDay = changed.end_date.startOf("day");
				const startDay = all.start_date.startOf("day");
				const e = endDay.isBefore(startDay, "day")
					? startDay
					: endDay;
				editForm.setFieldsValue({
					start_date: startDay,
					end_date: allDayEndOnCalendarDay(e),
				});
			}
		},
		[editForm],
	);

	const getCarTypeColor = useCallback(
		(carType) => {
			const carTypeId = carType?.id || carType;
			const carTypeObj =
				carTypes.find(
					(type) => type.id === carTypeId,
				) ||
				allCarTypes.find(
					(type) => type.id === carTypeId,
				);
			return carTypeObj?.color || "#2457e6";
		},
		[carTypes, allCarTypes],
	);

	const getCarTypeName = useCallback(
		(carType) => {
			const carTypeId = carType?.id || carType;
			const carTypeObj =
				carTypes.find(
					(type) => type.id === carTypeId,
				) ||
				allCarTypes.find(
					(type) => type.id === carTypeId,
				);
			return carTypeObj?.name || "-";
		},
		[carTypes, allCarTypes],
	);

	const getScheduleCarLabel = useCallback(
		(schedule) => {
			if (schedule.custom_car_number?.trim()) {
				return schedule.custom_car_number.trim();
			}
			return getCarTypeName(schedule.car_type);
		},
		[getCarTypeName],
	);

	const getInspectionCarLabel = useCallback(
		(inspection) => {
			if (inspection.custom_car_number?.trim()) {
				return inspection.custom_car_number.trim();
			}
			return getCarTypeName(inspection.car_type);
		},
		[getCarTypeName],
	);

	const getRepairCarLabel = getInspectionCarLabel;

	const scheduleLaneMap = useMemo(() => {
		const laneMap = {};
		const laneEndMs = [];

		const sortedSchedules = [...visibleCalendarItems].sort(
			compareCalendarItems,
		);

		sortedSchedules.forEach((schedule) => {
			const startMs = schedule._dayStart.valueOf();
			const endMs = schedule._dayEnd.valueOf();

			let laneIndex = laneEndMs.findIndex(
				(laneEnd) => startMs > laneEnd,
			);

			if (laneIndex === -1) {
				laneIndex = laneEndMs.length;
			}

			laneEndMs[laneIndex] = endMs;
			laneMap[schedule._calendarKey] = laneIndex;
		});

		return laneMap;
	}, [visibleCalendarItems]);

	const weekContentHeightByWeekStart = useMemo(() => {
		const countByWeek = {};

		for (const [dayKey, items] of calendarItemsByDay) {
			if (!items.length) {
				continue;
			}
			const weekStartKey = getSundayWeekStartKey(dayKey);

			if (isMobile) {
				countByWeek[weekStartKey] = Math.max(
					countByWeek[weekStartKey] || 0,
					items.length,
				);
				continue;
			}

			let maxLaneIndex = -1;
			for (const item of items) {
				const laneIndex =
					scheduleLaneMap[item._calendarKey] ?? 0;
				if (laneIndex > maxLaneIndex) {
					maxLaneIndex = laneIndex;
				}
			}
			const laneCount = maxLaneIndex + 1;
			countByWeek[weekStartKey] = Math.max(
				countByWeek[weekStartKey] || 0,
				laneCount,
			);
		}

		const heightByWeek = {};
		const rowHeight = isMobile
			? CALENDAR_MOBILE_TAG_ROW_HEIGHT
			: CALENDAR_LANE_ROW_HEIGHT;

		for (const [weekStartKey, count] of Object.entries(
			countByWeek,
		)) {
			heightByWeek[weekStartKey] = Math.max(
				CALENDAR_CONTENT_BASE_HEIGHT,
				count * rowHeight,
			);
		}

		return heightByWeek;
	}, [calendarItemsByDay, scheduleLaneMap, isMobile]);

	const getDateContentStyle = useCallback(
		(date) => {
			const weekStartKey = getSundayWeekStartKey(date);
			const minHeight =
				weekContentHeightByWeekStart[weekStartKey] ||
				CALENDAR_CONTENT_BASE_HEIGHT;
			return { minHeight };
		},
		[weekContentHeightByWeekStart],
	);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const y = calendarPanelDate.year();
				const m = calendarPanelDate.month() + 1;
				const map = await fetchKoreanHolidays(y, m);
				if (!cancelled) {
					setHolidayMap(map);
				}
			} catch (e) {
				console.error(e);
				if (!cancelled) {
					setHolidayMap({});
				}
				message.warning(
					"공휴일 정보를 불러오지 못했습니다. 주말만 구분됩니다.",
				);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [calendarPanelDate]);

	const renderDailySchedule = () => {
		const daySchedules = getDaySchedules(selectedDate);
		const dayInspectionSchedules =
			getDayInspectionSchedules(selectedDate);
		const dayRepairSchedules = getDayRepairSchedules(selectedDate);
		const hasSchedules = daySchedules.length > 0;
		const hasInspectionSchedules =
			dayInspectionSchedules.length > 0;
		const hasRepairSchedules = dayRepairSchedules.length > 0;
		const hasAnySchedules =
			hasSchedules ||
			hasInspectionSchedules ||
			hasRepairSchedules;

		return (
			<div className="utils-rent_sanctuary__daily-schedule">
				{hasSchedules && (
					<>
						<h4
							style={{
								marginBottom: 0,
								fontWeight: 600,
							}}>
							차량 예약
						</h4>
						<List
							dataSource={
								daySchedules
							}
							renderItem={(
								schedule,
							) => (
								<List.Item
									className="utils-rent_sanctuary__daily-schedule__item"
									style={{
										padding: "12px 0",
										borderBottom:
											"1px solid #f0f0f0",
									}}>
									<div
										className="utils-rent_sanctuary__daily-schedule__item-content"
										style={{
											display: "flex",
											flexWrap: "wrap",
											gap: "8px",
											width: "100%",
											alignItems: "center",
											justifyContent:
												"space-between",
										}}>
										<div
											style={{
												flex: 1,
											}}>
											<div className="utils-rent_sanctuary__daily-schedule__item-tags">
												<Tag
													color={getScheduleTypeTagColor(
														schedule.schedule_type ||
															SCHEDULE_TYPE_RENTAL,
													)}
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													{schedule.schedule_type ||
														SCHEDULE_TYPE_RENTAL}
												</Tag>
												<Tag
													color={getCarTypeColor(
														schedule.car_type,
													)}
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
														display: "flex",
														justifyContent:
															"center",
														alignItems: "center",
													}}>
													{getScheduleCarLabel(
														schedule,
													)}
												</Tag>
												<Tag
													color="blue"
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
														display: "flex",
														justifyContent:
															"center",
														alignItems: "center",
													}}>
													{formatScheduleRangeLabel(
														schedule,
													)}
												</Tag>
											</div>
											<div className="utils-rent_sanctuary__daily-schedule__item-info">
												<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
													대여
													내역:{" "}
													{
														schedule.title
													}
												</div>
												{schedule.driver ? (
													<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
														운전자:{" "}
														{
															schedule.driver
														}
													</div>
												) : null}
												{schedule.applicant_name ? (
													<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
														신청자:{" "}
														{
															schedule.applicant_name
														}
													</div>
												) : null}
												{schedule.applicant_contact ? (
													<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
														연락처:{" "}
														{
															schedule.applicant_contact
														}
													</div>
												) : null}
											</div>
										</div>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												gap: "8px",
											}}>
											<Button
												size="small"
												icon={
													<LinkOutlined />
												}
												onClick={() =>
													handleCopyShareLink(
														schedule,
													)
												}>
												링크
												복사
											</Button>
											<Button
												type="primary"
												icon={
													<EditOutlined />
												}
												size="small"
												onClick={() =>
													handleEditSchedule(
														schedule,
													)
												}>
												수정
											</Button>
											<Popconfirm
												title="정말 삭제하시겠습니까?"
												description="이 작업은 되돌릴 수 없습니다."
												onConfirm={() =>
													handleDeleteSchedule(
														schedule.id,
													)
												}
												okText="삭제"
												cancelText="취소"
												okButtonProps={{
													danger: true,
												}}>
												<Button
													type="primary"
													danger
													icon={
														<DeleteOutlined />
													}
													size="small">
													삭제
												</Button>
											</Popconfirm>
										</div>
									</div>
								</List.Item>
							)}
							style={{
								marginBottom:
									"16px",
							}}
						/>
					</>
				)}

				{hasInspectionSchedules && (
					<>
						<h4
							style={{
								marginBottom: 0,
								fontWeight: 600,
							}}>
							검사 일정
						</h4>
						<List
							dataSource={
								dayInspectionSchedules
							}
							renderItem={(
								inspection,
							) => (
								<List.Item
									className="utils-rent_sanctuary__daily-schedule__item"
									style={{
										padding: "12px 0",
										borderBottom:
											"1px solid #f0f0f0",
									}}>
									<div
										className="utils-rent_sanctuary__daily-schedule__item-content"
										style={{
											display: "flex",
											flexWrap: "wrap",
											gap: "8px",
											width: "100%",
											alignItems: "center",
											justifyContent:
												"space-between",
										}}>
										<div
											style={{
												flex: 1,
											}}>
											<div className="utils-rent_sanctuary__daily-schedule__item-tags">
												<Tag
													color="orange"
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													검사
												</Tag>
												{inspection.is_completed ? (
													<Tag
														color="green"
														style={{
															fontSize: isMobile
																? "13px"
																: "14px",
															padding: "4px 12px",
														}}>
														완료
													</Tag>
												) : null}
												<Tag
													color={getCarTypeColor(
														inspection.car_type,
													)}
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													{getInspectionCarLabel(
														inspection,
													)}
												</Tag>
												<Tag
													color="blue"
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													검사
													기한{" "}
													{dayjs(
														inspection.available_from,
													).format(
														"YYYY-MM-DD",
													)}
												</Tag>
											</div>
											<div className="utils-rent_sanctuary__daily-schedule__item-info">
												<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
													검사
													마감일:{" "}
													{dayjs(
														inspection.inspection_due_date,
													).format(
														"YYYY-MM-DD",
													)}
												</div>
												{inspection.assignee ? (
													<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
														담당자:{" "}
														{
															inspection.assignee
														}
													</div>
												) : null}
												{inspection.is_completed &&
												inspection.completed_at ? (
													<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
														완료일:{" "}
														{dayjs(
															inspection.completed_at,
														).format(
															"YYYY-MM-DD",
														)}
													</div>
												) : null}
											</div>
										</div>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												gap: "8px",
											}}>
											<Button
												type="default"
												size="small"
												disabled={
													loading
												}
												onClick={() =>
													handleOpenCompleteInspection(
														inspection,
													)
												}>
												검사
												완료
											</Button>
											<Button
												type="primary"
												icon={
													<EditOutlined />
												}
												size="small"
												onClick={() =>
													handleEditInspectionSchedule(
														inspection,
													)
												}>
												수정
											</Button>
											<Popconfirm
												title="정말 삭제하시겠습니까?"
												description="이 작업은 되돌릴 수 없습니다."
												onConfirm={() =>
													handleDeleteInspectionSchedule(
														inspection.id,
													)
												}
												okText="삭제"
												cancelText="취소"
												okButtonProps={{
													danger: true,
												}}>
												<Button
													type="primary"
													danger
													icon={
														<DeleteOutlined />
													}
													size="small">
													삭제
												</Button>
											</Popconfirm>
										</div>
									</div>
								</List.Item>
							)}
							style={{
								marginBottom:
									"16px",
							}}
						/>
					</>
				)}

				{hasRepairSchedules && (
					<>
						<h4
							style={{
								marginBottom: 0,
								fontWeight: 600,
							}}>
							수리 일정
						</h4>
						<List
							dataSource={
								dayRepairSchedules
							}
							renderItem={(
								repair,
							) => (
								<List.Item
									className="utils-rent_sanctuary__daily-schedule__item"
									style={{
										padding: "12px 0",
										borderBottom:
											"1px solid #f0f0f0",
									}}>
									<div
										className="utils-rent_sanctuary__daily-schedule__item-content"
										style={{
											display: "flex",
											flexWrap: "wrap",
											gap: "8px",
											width: "100%",
											alignItems: "center",
											justifyContent:
												"space-between",
										}}>
										<div
											style={{
												flex: 1,
											}}>
											<div className="utils-rent_sanctuary__daily-schedule__item-tags">
												<Tag
													color="magenta"
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													수리
												</Tag>
												<Tag
													color={getCarTypeColor(
														repair.car_type,
													)}
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													{getRepairCarLabel(
														repair,
													)}
												</Tag>
												<Tag
													color="blue"
													style={{
														fontSize: isMobile
															? "13px"
															: "14px",
														padding: "4px 12px",
													}}>
													{dayjs(
														repair.repair_start_date,
													).format(
														"YYYY-MM-DD",
													)}{" "}
													~{" "}
													{dayjs(
														repair.repair_end_date,
													).format(
														"YYYY-MM-DD",
													)}
												</Tag>
											</div>
											<div className="utils-rent_sanctuary__daily-schedule__item-info">
												{repair.assignee ? (
													<div className="utils-rent_sanctuary__daily-schedule__item-info-item">
														담당자:{" "}
														{
															repair.assignee
														}
													</div>
												) : null}
											</div>
										</div>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												gap: "8px",
											}}>
											<Button
												type="primary"
												icon={
													<EditOutlined />
												}
												size="small"
												onClick={() =>
													handleEditRepairSchedule(
														repair,
													)
												}>
												수정
											</Button>
											<Popconfirm
												title="정말 삭제하시겠습니까?"
												description="이 작업은 되돌릴 수 없습니다."
												onConfirm={() =>
													handleDeleteRepairSchedule(
														repair.id,
													)
												}
												okText="삭제"
												cancelText="취소"
												okButtonProps={{
													danger: true,
												}}>
												<Button
													type="primary"
													danger
													icon={
														<DeleteOutlined />
													}
													size="small">
													삭제
												</Button>
											</Popconfirm>
										</div>
									</div>
								</List.Item>
							)}
							style={{
								marginBottom:
									"16px",
							}}
						/>
					</>
				)}

				{!hasAnySchedules && (
					<p className="utils-rent_sanctuary__daily-schedule__empty">
						해당 날짜의 일정이 없습니다.
					</p>
				)}
				<div
					className="utils-rent_sanctuary__daily-schedule__actions"
					style={{
						marginTop: "20px",
						textAlign: "center",
					}}>
					<Space
						wrap
						size={[12, 12]}
						style={{
							justifyContent:
								"center",
						}}>
						{isRentalMode ? (
							<Button
								type="primary"
								size={
									isMobile
										? "middle"
										: "large"
								}
								onClick={
									handleAddSchedule
								}
								className="utils-rent_sanctuary__daily-schedule__apply-button"
								style={{
									width: isMobile
										? "100%"
										: "auto",
									minWidth: "150px",
								}}>
								차량 예약 추가
							</Button>
						) : null}
						{isMaintenanceMode ? (
							<Button
								type="primary"
								size={
									isMobile
										? "middle"
										: "large"
								}
								onClick={
									handleAddInspectionSchedule
								}
								style={{
									width: isMobile
										? "100%"
										: "auto",
									minWidth: "150px",
								}}>
								검사 일정 추가
							</Button>
						) : null}
					</Space>
				</div>
			</div>
		);
	};

	const renderCalendarDateBody = useCallback(
		(date) => {
			const daySchedules = getDayCalendarItems(date);
			const hasSchedules = daySchedules.length > 0;
			const cellPaddingStyle = {
				// paddingLeft: hasSingleDaySchedule || hasStartSchedule ? "4px" : "0",
				// paddingRight: hasSingleDaySchedule || hasEndSchedule ? "4px" : "0",
				paddingLeft: 0,
				paddingRight: 0,
			};

			if (!hasSchedules) {
				return null;
			}

			if (isMobile) {
				return (
					<div
						className="utils-rent_sanctuary__calendar-cell"
						style={{
							...cellPaddingStyle,
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: 2,
							width: "100%",
						}}>
						{daySchedules.map(
							(schedule) => (
								<Tag
									key={
										schedule._calendarKey
									}
									color={
										isInspectionCalendarItem(
											schedule,
										)
											? schedule.is_completed
												? "#bfbfbf"
												: "#fe4d4d"
											: isRepairCalendarItem(
														schedule,
												  )
												? "magenta"
												: getCarTypeColor(
														schedule.car_type,
													)
									}
									style={{
										margin: 0,
										maxWidth: "100%",
										fontSize: "10px",
										lineHeight: 1.2,
										padding: "0 4px",
										display: "flex",
										alignItems: "center",
										justifyContent:
											"center",
									}}>
									{truncateCellTagText(
										isInspectionCalendarItem(
											schedule,
										)
											? getInspectionCarLabel(
													schedule,
												)
											: isRepairCalendarItem(
														schedule,
												  )
												? getRepairCarLabel(
														schedule,
													)
												: getScheduleCarLabel(
														schedule,
													),
									)}
								</Tag>
							),
						)}
					</div>
				);
			}

			return (
				<div
					className="utils-rent_sanctuary__calendar-cell"
					style={cellPaddingStyle}>
					{(() => {
						const daySchedulesByLane =
							daySchedules.reduce(
								(
									acc,
									schedule,
								) => {
									const laneIndex =
										scheduleLaneMap[
											schedule
												._calendarKey
										] ??
										0;
									acc[
										laneIndex
									] =
										schedule;
									return acc;
								},
								{},
							);

						const laneIndexes =
							Object.keys(
								daySchedulesByLane,
							).map(Number);
						const maxLaneIndex =
							laneIndexes.length > 0
								? Math.max(
										...laneIndexes,
									)
								: -1;

						return Array.from(
							{
								length:
									maxLaneIndex +
									1,
							},
							(_, laneIndex) => {
								const schedule =
									daySchedulesByLane[
										laneIndex
									];

								if (!schedule) {
									return (
										<div
											key={`${date.format("YYYY-MM-DD")}-empty-${laneIndex}`}
											style={{
												height: "24.75px",
												marginBottom:
													"2px",
											}}
										/>
									);
								}

								const scheduleStartDate =
									schedule._dayStart;
								const scheduleEndDate =
									schedule._dayEnd;
								const isStartDate =
									date.isSame(
										scheduleStartDate,
										"day",
									);
								const isEndDate =
									date.isSame(
										scheduleEndDate,
										"day",
									);
								const isSingleDay =
									isStartDate &&
									isEndDate;
								const isMiddleDate =
									!isStartDate &&
									!isEndDate;
								const carTypeColor =
									isInspectionCalendarItem(
										schedule,
									)
										? schedule.is_completed
											? "#bfbfbf"
											: "#fe4d4d"
										: isRepairCalendarItem(
													schedule,
											  )
											? "#eb2f96"
											: getCarTypeColor(
													schedule.car_type,
												);
								const barTimeParen =
									isInspectionCalendarItem(
										schedule,
									) ||
									isRepairCalendarItem(
										schedule,
									)
										? ""
										: formatCalendarBarTimeParen(
												schedule,
											);
								const driverSuffix =
									!isInspectionCalendarItem(
										schedule,
									) &&
									!isRepairCalendarItem(
										schedule,
									) &&
									schedule.driver
										? ` · ${schedule.driver}`
										: "";
								const calendarBarTitleTail =
									isInspectionCalendarItem(
										schedule,
									)
										? `검사${schedule.is_completed ? " · 완료" : ""}${schedule.assignee ? ` · ${schedule.assignee}` : ""}`
										: isRepairCalendarItem(
													schedule,
											  )
											? `수리${schedule.assignee ? ` · ${schedule.assignee}` : ""}`
											: barTimeParen
												? `${schedule.title}${driverSuffix} ${barTimeParen}`
												: `${schedule.title}${driverSuffix}`;

								// 여러 날짜에 걸친 일정을 시작일의 같은 줄에 유지
								return (
									<div
										key={
											schedule._calendarKey
										}
										className="utils-rent_sanctuary__calendar-event"
										style={{
											backgroundColor:
												carTypeColor,
											color: "#fff",
											padding: "2px 6px",
											marginBottom:
												"2px",
											fontSize: "11px",
											borderRadius:
												isSingleDay
													? "4px"
													: isStartDate
														? "4px 0 0 4px"
														: isEndDate
															? "0 4px 4px 0"
															: "0",
											marginLeft: isStartDate
												? "4px"
												: "0",
											marginRight:
												isEndDate
													? "4px"
													: "0",
											width:
												(isStartDate &&
													!isSingleDay) ||
												(isEndDate &&
													!isSingleDay)
													? "calc(100% - 4px)"
													: isSingleDay
														? "calc(100% - 8px)"
														: "100%",
											borderLeft: isStartDate
												? "3px solid #666"
												: "none",
											position: "relative",
											zIndex: 1,
											overflow: "hidden",
											textOverflow:
												"ellipsis",
											whiteSpace: "nowrap",
										}}>
										{isStartDate && (
											<span
												style={{
													fontWeight: 500,
													fontSize: "1.2em",
												}}>
												{isInspectionCalendarItem(
													schedule,
												)
													? getInspectionCarLabel(
															schedule,
														)
													: isRepairCalendarItem(
																schedule,
														  )
														? getRepairCarLabel(
																schedule,
															)
														: getScheduleCarLabel(
																schedule,
															)}

												:{" "}
												{
													calendarBarTitleTail
												}
											</span>
										)}
										{isMiddleDate && (
											<span
												style={{
													opacity: 0,
													fontSize: "1.2em",
												}}>
												{
													calendarBarTitleTail
												}
											</span>
										)}
										{isEndDate &&
											!isStartDate &&
											!isSingleDay && (
												<span
													style={{
														opacity: 0,
														fontSize: "1.2em",
													}}>
													{
														calendarBarTitleTail
													}
												</span>
											)}
									</div>
								);
							},
						);
					})()}
				</div>
			);
		},
		[
			getDayCalendarItems,
			isMobile,
			scheduleLaneMap,
			getCarTypeColor,
			getCarTypeName,
			getScheduleCarLabel,
			getInspectionCarLabel,
			getRepairCarLabel,
		],
	);

	const { fullCellRender } = useMemo(
		() =>
			createCalendarHolidayRenders({
				holidayMap,
				renderDateContent: renderCalendarDateBody,
				getDateContentStyle,
			}),
		[holidayMap, renderCalendarDateBody, getDateContentStyle],
	);

	const exportListToExcel = useCallback(() => {
		try {
			const headerStyle = {
				font: { bold: true },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};
			const dataStyle = {
				alignment: { vertical: "center" },
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};
			const cell = (v) => ({
				v: v == null || v === "" ? "" : String(v),
				t: "s",
				s: dataStyle,
			});
			const header = (v) => ({ v, t: "s", s: headerStyle });

			let headerRow;
			let dataRows;
			if (isMaintenanceMode) {
				headerRow = [
					header("구분"),
					header("완료 여부"),
					header("담당자"),
					header("기간"),
					header("제목"),
				];
				dataRows = filteredListViewRows.map((row) => [
					cell(scheduleCarLabel(row)),
					cell(row.is_completed ? "완료" : ""),
					cell(row.assignee || ""),
					cell(formatListPeriodLabel(row)),
					cell(row.title || ""),
				]);
			} else {
				headerRow = [
					header("구분"),
					header("차량번호"),
					header("운전자"),
					header("신청자"),
					header("신청자 연락처"),
					header("기간"),
					header("제목"),
				];
				dataRows = filteredListViewRows.map((row) => [
					cell(row.schedule_type || "대여"),
					cell(scheduleCarLabel(row)),
					cell(row.driver || ""),
					cell(row.applicant_name || ""),
					cell(row.applicant_contact || ""),
					cell(formatListPeriodLabel(row)),
					cell(row.title || ""),
				]);
			}

			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.aoa_to_sheet([
				headerRow,
				...dataRows,
			]);
			XLSX.utils.book_append_sheet(
				wb,
				ws,
				isMaintenanceMode ? "검사·수리" : "대여·운행",
			);
			const prefix = isMaintenanceMode
				? "차량_검사수리_일정"
				: "차량_대여운행_일정";
			const rangeLabel =
				listRange?.[0] && listRange?.[1]
					? `${listRange[0].format("YYYY-MM-DD")}_${listRange[1].format("YYYY-MM-DD")}`
					: "전체";
			XLSX.writeFile(wb, `${prefix}_${rangeLabel}.xlsx`);
		} catch (error) {
			message.error("엑셀 다운로드 중 오류가 발생했습니다.");
		}
	}, [
		filteredListViewRows,
		isMaintenanceMode,
		listRange,
		scheduleCarLabel,
	]);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle
				title={
					isMaintenanceMode
						? "차량 검사·수리 일정"
						: "차량 대여 및 운행 일정"
				}
			/>
			<Layout className="member-layout">
				<AccessedCarNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container">
							<div className="utils-rent_sanctuary__header">
								<h1 className="utils-accessed_car__header__title">
									{isMaintenanceMode
										? "차량 검사·수리 일정"
										: "차량 대여 및 운행 일정"}
								</h1>
							</div>
							<div className="utils-rent_sanctuary__content-container utils-rent-car-schedule-stack">
								<Tabs
									activeKey={
										viewMode
									}
									onChange={
										setViewMode
									}
									items={[
										{
											key: "calendar",
											label: "캘린더뷰",
										},
										{
											key: "list",
											label: "리스트뷰",
										},
									]}
									style={{
										marginBottom: 8,
										width: "100%",
									}}
								/>
								{viewMode ===
								"list" ? (
									<Card
										style={{
											width: "100%",
										}}>
										<Space
											wrap
											style={{
												marginBottom: 16,
												width: "100%",
												justifyContent:
													"space-between",
											}}>
											<DatePicker.RangePicker
												inputReadOnly={isMobile}
												value={
													listRange
												}
												onChange={
													setListRange
												}
												format="YYYY-MM-DD"
												allowClear
												placeholder={[
													"시작일",
													"종료일",
												]}
												presets={[
													{
														label: "이번 달",
														value: [
															dayjs().startOf(
																"month",
															),
															dayjs().endOf(
																"month",
															),
														],
													},
													{
														label: "지난 달",
														value: [
															dayjs()
																.subtract(
																	1,
																	"month",
																)
																.startOf(
																	"month",
																),
															dayjs()
																.subtract(
																	1,
																	"month",
																)
																.endOf(
																	"month",
																),
														],
													},
													{
														label: "다음 달",
														value: [
															dayjs()
																.add(
																	1,
																	"month",
																)
																.startOf(
																	"month",
																),
															dayjs()
																.add(
																	1,
																	"month",
																)
																.endOf(
																	"month",
																),
														],
													},
													{
														label: "올해",
														value: [
															dayjs().startOf(
																"year",
															),
															dayjs().endOf(
																"year",
															),
														],
													},
												]}
											/>
											<Space
												wrap>
												<Input.Search
													allowClear
													placeholder={
														isMaintenanceMode
															? "차량번호, 담당자"
															: "차량번호, 운전자, 신청자"
													}
													style={{
														minWidth: 260,
													}}
													value={
														listSearch
													}
													onChange={(
														e,
													) =>
														setListSearch(
															e
																.target
																.value,
														)
													}
												/>
												<Button
													icon={
														<DownloadOutlined />
													}
													onClick={
														exportListToExcel
													}
													disabled={
														!filteredListViewRows.length
													}>
													엑셀
													내보내기
												</Button>
											</Space>
										</Space>
										<Table
											rowKey={(
												row,
											) =>
												row._calendarKey ||
												`${row.id}-${row._listKind || row.schedule_type}`
											}
											loading={
												loading
											}
											dataSource={
												filteredListViewRows
											}
											onChange={(
												_pagination,
												filters,
											) => {
												setListScheduleTypeFilters(
													filters.schedule_type ||
														[],
												);
											}}
											pagination={{
												pageSize: 20,
											}}
											columns={
												isMaintenanceMode
													? [
															{
																title: "구분",
																key: "car",
																render: (
																	_,
																	row,
																) =>
																	scheduleCarLabel(
																		row,
																	),
															},
															{
																title: "완료 여부",
																dataIndex: "is_completed",
																render: (
																	v,
																) =>
																	v
																		? "완료"
																		: "",
															},
															{
																title: "담당자",
																dataIndex: "assignee",
																render: (
																	v,
																) =>
																	v ||
																	"-",
															},
															{
																title: "기간",
																key: "period",
																render: (
																	_,
																	row,
																) =>
																	formatListPeriodLabel(
																		row,
																	),
															},
														]
													: [
															{
																title: "구분",
																dataIndex: "schedule_type",
																width: 80,
																filters: [
																	{
																		text: "대여",
																		value: SCHEDULE_TYPE_RENTAL,
																	},
																	{
																		text: "운행",
																		value: SCHEDULE_TYPE_OPERATION,
																	},
																],
																filteredValue:
																	listScheduleTypeFilters,
																render: (
																	v,
																) =>
																	v ||
																	"대여",
															},
															{
																title: "차량번호",
																key: "car",
																render: (
																	_,
																	row,
																) =>
																	scheduleCarLabel(
																		row,
																	),
															},
															{
																title: "운전자",
																dataIndex: "driver",
																render: (
																	v,
																) =>
																	v ||
																	"-",
															},
															{
																title: "신청자",
																dataIndex: "applicant_name",
																render: (
																	v,
																) =>
																	v ||
																	"-",
															},
															{
																title: "신청자 연락처",
																dataIndex: "applicant_contact",
																render: (
																	v,
																) =>
																	v ||
																	"-",
															},
															{
																title: "기간",
																key: "period",
																render: (
																	_,
																	row,
																) =>
																	formatListPeriodLabel(
																		row,
																	),
															},
															{
																title: "제목",
																dataIndex: "title",
																ellipsis: true,
															},
														]
											}
											sticky={{
												offsetHeader: 64,
											}}
										/>
									</Card>
								) : (
									<Card
										className="utils-rent_sanctuary__calendar-card"
										style={{
											width: "100%",
										}}>
										{isRentalMode ? (
											<Card
												size="small"
												style={{
													marginBottom: 16,
													border: "none",
												}}>
												<Space
													wrap
													size={[
														16,
														8,
													]}
													style={{
														display: "flex",
														justifyContent:
															"center",
														alignItems: "center",
														gap: 16,
													}}>
													<Checkbox
														checked={
															showRentalSchedules
														}
														onChange={(
															e,
														) =>
															setShowRentalSchedules(
																e
																	.target
																	.checked,
															)
														}>
														대여
													</Checkbox>
													<Checkbox
														checked={
															showOperationSchedules
														}
														onChange={(
															e,
														) =>
															setShowOperationSchedules(
																e
																	.target
																	.checked,
															)
														}>
														운행
													</Checkbox>
												</Space>
											</Card>
										) : null}
										<div className="utils-rent_car_calendar-scope">
											<Calendar
												className="utils-calendar-hide-outside-month-week utils-rent_car_calendar-expand-weeks"
												headerRender={({
													value,
													onChange,
												}) => {
													const year =
														value.year();
													const month =
														value.month();
													const months =
														[];
													const yearOptions =
														[];

													// Generate month options
													for (
														let i = 0;
														i <
														12;
														i++
													) {
														months.push(
															<Select.Option
																key={
																	i
																}
																value={
																	i
																}>
																{i +
																	1}

																월
															</Select.Option>,
														);
													}

													// Generate year options (current year ± 10 years)
													for (
														let i =
															year -
															10;
														i <
														year +
															10;
														i++
													) {
														yearOptions.push(
															<Select.Option
																key={
																	i
																}
																value={
																	i
																}>
																{
																	i
																}

																년
															</Select.Option>,
														);
													}

													return (
														<Space
															style={{
																padding: 8,
															}}>
															<Button
																size="middle"
																onClick={() => {
																	onChange(
																		value.subtract(
																			1,
																			"month",
																		),
																	);
																	setModalVisible(
																		false,
																	);
																}}>
																<LeftOutlined />
															</Button>
															<Select
																size="middle"
																value={
																	year
																}
																onChange={(
																	newYear,
																) => {
																	const now =
																		value
																			.clone()
																			.year(
																				newYear,
																			);
																	onChange(
																		now,
																	);
																}}
																style={{
																	width: 100,
																}}>
																{
																	yearOptions
																}
															</Select>
															<Select
																size="middle"
																value={
																	month
																}
																onChange={(
																	newMonth,
																) => {
																	const now =
																		value
																			.clone()
																			.month(
																				newMonth,
																			);
																	onChange(
																		now,
																	);
																}}
																style={{
																	width: 80,
																}}>
																{
																	months
																}
															</Select>
															<Button
																size="middle"
																onClick={() =>
																	onChange(
																		dayjs(),
																	)
																}>
																오늘
															</Button>
															<Button
																size="middle"
																onClick={() => {
																	onChange(
																		value.add(
																			1,
																			"month",
																		),
																	);
																	setModalVisible(
																		false,
																	);
																}}>
																<RightOutlined />
															</Button>
														</Space>
													);
												}}
												fullCellRender={
													fullCellRender
												}
												onPanelChange={(
													d,
												) => {
													setCalendarPanelDate(
														d,
													);
												}}
												onSelect={
													handleDateSelect
												}
												mode="month"
												locale={{
													lang: {
														locale: "ko",
														monthBeforeYear: true,
														yearFormat: "YYYY년",
														monthFormat:
															"M월",
														weekFormat: "wo",
														weekStart: 0,
														shortWeekDays:
															[
																"일",
																"월",
																"화",
																"수",
																"목",
																"금",
																"토",
															],
														shortMonths:
															[
																"1월",
																"2월",
																"3월",
																"4월",
																"5월",
																"6월",
																"7월",
																"8월",
																"9월",
																"10월",
																"11월",
																"12월",
															],
													},
												}}
											/>
										</div>
									</Card>
								)}
							</div>
						</div>
						<Modal
							title={
								<div
									style={{
										fontSize: isMobile
											? "16px"
											: "18px",
										fontWeight: 600,
									}}>
									{selectedDate.format(
										"YYYY년 MM월 DD일",
									)}{" "}
									일정
								</div>
							}
							open={modalVisible}
							onCancel={() =>
								setModalVisible(
									false,
								)
							}
							footer={null}
							width={
								isMobile
									? "95%"
									: 800
							}
							zIndex={1000}
							style={{
								top: isMobile
									? 10
									: 20,
								maxWidth: isMobile
									? "100%"
									: 800,
							}}
							className="utils-rent_sanctuary__schedule-modal"
							destroyOnClose={true}
							maskClosable={true}
							centered={isMobile}>
							<div
								style={{
									maxHeight: isMobile
										? "80vh"
										: "70vh",
									overflowY: "auto",
									padding: "8px 0",
								}}>
								{renderDailySchedule()}
							</div>
						</Modal>

						{/* 차량 예약 추가 모달 */}
						<Modal
							title="차량 예약 추가"
							open={addModalVisible}
							onCancel={() => {
								setAddModalVisible(
									false,
								);
								form.resetFields();
							}}
							footer={null}
							width={
								isMobile
									? "95%"
									: 600
							}
							zIndex={1001}
							centered={isMobile}>
							<Form
								form={form}
								layout="vertical"
								onFinish={
									handleAddSubmit
								}
								onValuesChange={
									syncAddFormAllDay
								}>
								<Form.Item
									name="start_date"
									label="시작 일시"
									dependencies={[
										"all_day",
									]}
									rules={[
										{
											required: true,
											message: "시작 일시를 선택해주세요",
										},
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												if (
													!value ||
													getFieldValue(
														"all_day",
													)
												) {
													return Promise.resolve();
												}
												const m =
													value.minute();
												if (
													m !==
														0 &&
													m !==
														30
												) {
													return Promise.reject(
														new Error(
															"분은 00분 또는 30분만 선택할 수 있습니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
									]}>
									<AllDayAwareDatePicker
										inputReadOnly={isMobile}
										form={
											form
										}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD HH:mm"
										placeholder="시작 일시를 선택하세요"
									/>
								</Form.Item>
								<Form.Item
									name="end_date"
									label="종료 일시"
									dependencies={[
										"all_day",
									]}
									rules={[
										{
											required: true,
											message: "종료 일시를 선택해주세요",
										},
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												if (
													!value ||
													!getFieldValue(
														"start_date",
													)
												) {
													return Promise.resolve();
												}
												if (
													!value.isAfter(
														getFieldValue(
															"start_date",
														),
													)
												) {
													return Promise.reject(
														new Error(
															"종료 일시는 시작 일시보다 이후여야 합니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												if (
													!value ||
													getFieldValue(
														"all_day",
													)
												) {
													return Promise.resolve();
												}
												const m =
													value.minute();
												if (
													m !==
														0 &&
													m !==
														30
												) {
													return Promise.reject(
														new Error(
															"분은 00분 또는 30분만 선택할 수 있습니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
									]}>
									<AllDayAwareDatePicker
										inputReadOnly={isMobile}
										form={
											form
										}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD HH:mm"
										placeholder="종료 일시를 선택하세요"
									/>
								</Form.Item>
								<Form.Item
									name="all_day"
									valuePropName="checked"
									style={{
										marginBottom: 16,
									}}>
									<Checkbox>
										종일
									</Checkbox>
								</Form.Item>
								<Form.Item
									name="schedule_type"
									label="종류"
									rules={[
										{
											required: true,
											message: "종류를 선택해주세요",
										},
									]}>
									<Select placeholder="종류를 선택하세요">
										<Option
											value={
												SCHEDULE_TYPE_RENTAL
											}>
											대여
										</Option>
										<Option
											value={
												SCHEDULE_TYPE_OPERATION
											}>
											운행
										</Option>
									</Select>
								</Form.Item>
								<Form.Item
									name="title"
									label="대여 내역"
									rules={[
										{
											required: true,
											message: "대여 내역을 입력해주세요",
										},
									]}>
									<Input placeholder="대여 내역을 입력하세요" />
								</Form.Item>
								<DriverFields drivers={drivers} />
								<Form.Item
									name="applicant_name"
									label="신청자 이름">
									<Input placeholder="신청자 이름 (선택)" />
								</Form.Item>
								<Form.Item
									name="applicant_contact"
									label="연락처">
									<Input placeholder="연락처 (선택)" />
								</Form.Item>
								<ScheduleCarTypeFields
									form={
										form
									}
									carTypes={
										carTypes
									}
									allCarTypes={
										allCarTypes
									}
								/>
								<Form.Item>
									<Button
										type="primary"
										htmlType="submit"
										loading={
											loading
										}
										block>
										추가하기
									</Button>
								</Form.Item>
							</Form>
						</Modal>

						{/* 차량 예약 수정 모달 */}
						<Modal
							title="차량 예약 수정"
							open={editModalVisible}
							onCancel={() => {
								setEditModalVisible(
									false,
								);
								setEditingSchedule(
									null,
								);
								editForm.resetFields();
							}}
							footer={null}
							width={
								isMobile
									? "95%"
									: 600
							}
							zIndex={1001}
							centered={isMobile}>
							<Form
								form={editForm}
								layout="vertical"
								onFinish={
									handleEditSubmit
								}
								onValuesChange={
									syncEditFormAllDay
								}>
								<Form.Item
									name="start_date"
									label="시작 일시"
									dependencies={[
										"all_day",
									]}
									rules={[
										{
											required: true,
											message: "시작 일시를 선택해주세요",
										},
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												if (
													!value ||
													getFieldValue(
														"all_day",
													)
												) {
													return Promise.resolve();
												}
												const m =
													value.minute();
												if (
													m !==
														0 &&
													m !==
														30
												) {
													return Promise.reject(
														new Error(
															"분은 00분 또는 30분만 선택할 수 있습니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
									]}>
									<AllDayAwareDatePicker
										inputReadOnly={isMobile}
										form={
											editForm
										}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD HH:mm"
										placeholder="시작 일시를 선택하세요"
									/>
								</Form.Item>
								<Form.Item
									name="end_date"
									label="종료 일시"
									dependencies={[
										"all_day",
									]}
									rules={[
										{
											required: true,
											message: "종료 일시를 선택해주세요",
										},
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												if (
													!value ||
													!getFieldValue(
														"start_date",
													)
												) {
													return Promise.resolve();
												}
												if (
													!value.isAfter(
														getFieldValue(
															"start_date",
														),
													)
												) {
													return Promise.reject(
														new Error(
															"종료 일시는 시작 일시보다 이후여야 합니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												if (
													!value ||
													getFieldValue(
														"all_day",
													)
												) {
													return Promise.resolve();
												}
												const m =
													value.minute();
												if (
													m !==
														0 &&
													m !==
														30
												) {
													return Promise.reject(
														new Error(
															"분은 00분 또는 30분만 선택할 수 있습니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
									]}>
									<AllDayAwareDatePicker
										inputReadOnly={isMobile}
										form={
											editForm
										}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD HH:mm"
										placeholder="종료 일시를 선택하세요"
									/>
								</Form.Item>
								<Form.Item
									name="all_day"
									valuePropName="checked"
									style={{
										marginBottom: 16,
									}}>
									<Checkbox>
										종일
									</Checkbox>
								</Form.Item>
								<Form.Item
									name="schedule_type"
									label="종류"
									rules={[
										{
											required: true,
											message: "종류를 선택해주세요",
										},
									]}>
									<Select placeholder="종류를 선택하세요">
										<Option
											value={
												SCHEDULE_TYPE_RENTAL
											}>
											대여
										</Option>
										<Option
											value={
												SCHEDULE_TYPE_OPERATION
											}>
											운행
										</Option>
									</Select>
								</Form.Item>
								<Form.Item
									name="title"
									label="대여 내역"
									rules={[
										{
											required: true,
											message: "대여 내역을 입력해주세요",
										},
									]}>
									<Input placeholder="대여 내역을 입력하세요" />
								</Form.Item>
								<DriverFields drivers={drivers} />
								<Form.Item
									name="applicant_name"
									label="신청자 이름">
									<Input placeholder="신청자 이름 (선택)" />
								</Form.Item>
								<Form.Item
									name="applicant_contact"
									label="연락처">
									<Input placeholder="연락처 (선택)" />
								</Form.Item>
								<ScheduleCarTypeFields
									form={
										editForm
									}
									carTypes={
										carTypes
									}
									allCarTypes={
										allCarTypes
									}
								/>
								<Form.Item>
									<Button
										type="primary"
										htmlType="submit"
										loading={
											loading
										}
										block>
										수정하기
									</Button>
								</Form.Item>
							</Form>
						</Modal>

						<Modal
							title={
								editingInspection
									? "검사 일정 수정"
									: "검사 일정 등록"
							}
							open={
								inspectionModalVisible
							}
							onCancel={() => {
								setInspectionModalVisible(
									false,
								);
								setEditingInspection(
									null,
								);
								inspectionForm.resetFields();
							}}
							footer={null}
							width={
								isMobile
									? "95%"
									: 600
							}
							zIndex={1001}
							centered={isMobile}
							destroyOnClose>
							<Form
								form={
									inspectionForm
								}
								layout="vertical"
								onFinish={
									handleInspectionSubmit
								}>
								<Form.Item
									name="custom_car_number"
									label="차량 종류"
									rules={[
										{
											required: true,
											message: "차량 종류를 입력하세요.",
										},
									]}>
									<Input placeholder="차량 종류를 입력하세요" />
								</Form.Item>
								<Form.Item
									name="inspection_date"
									label="검사 기한"
									rules={[
										{
											required: true,
											message: "검사 기한을 선택하세요.",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
								<Form.Item
									name="inspection_due_date"
									label="검사 마감일"
									rules={[
										{
											required: true,
											message: "검사 마감일을 선택하세요.",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
								<Form.Item
									name="assignee"
									label="담당자">
									<Input placeholder="담당자 이름 (선택)" />
								</Form.Item>
								<Form.Item>
									<Button
										type="primary"
										htmlType="submit"
										loading={
											loading
										}
										block>
										{editingInspection
											? "저장"
											: "등록"}
									</Button>
								</Form.Item>
							</Form>
						</Modal>

						<Modal
							title={
								editingRepair
									? "수리 일정 수정"
									: "수리 일정 추가"
							}
							open={
								repairModalVisible
							}
							onCancel={() => {
								setRepairModalVisible(
									false,
								);
								setEditingRepair(
									null,
								);
								repairForm.resetFields();
							}}
							footer={null}
							width={
								isMobile
									? "95%"
									: 600
							}
							zIndex={1001}
							centered={isMobile}
							destroyOnClose>
							<Form
								form={
									repairForm
								}
								layout="vertical"
								onFinish={
									handleRepairSubmit
								}>
								<Form.Item
									name="custom_car_number"
									label="차량 번호"
									rules={[
										{
											required: true,
											message: "차량 번호를 입력하세요.",
										},
									]}>
									<Input placeholder="차량 번호를 입력하세요" />
								</Form.Item>
								<Form.Item
									name="repair_start_date"
									label="수리 시작일"
									rules={[
										{
											required: true,
											message: "수리 시작일을 선택하세요.",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
								<Form.Item
									name="repair_end_date"
									label="수리 종료일"
									dependencies={[
										"repair_start_date",
									]}
									rules={[
										{
											required: true,
											message: "수리 종료일을 선택하세요.",
										},
										({
											getFieldValue,
										}) => ({
											validator(
												_,
												value,
											) {
												const start =
													getFieldValue(
														"repair_start_date",
													);
												if (
													!value ||
													!start
												) {
													return Promise.resolve();
												}
												if (
													value.isBefore(
														start,
														"day",
													)
												) {
													return Promise.reject(
														new Error(
															"수리 종료일은 시작일과 같거나 이후여야 합니다.",
														),
													);
												}
												return Promise.resolve();
											},
										}),
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
								<Form.Item
									name="assignee"
									label="담당자">
									<Input placeholder="담당자 이름 (선택)" />
								</Form.Item>
								<Form.Item>
									<Button
										type="primary"
										htmlType="submit"
										loading={
											loading
										}
										block>
										{editingRepair
											? "저장"
											: "등록"}
									</Button>
								</Form.Item>
							</Form>
						</Modal>
						<Modal
							title="검사 완료"
							open={
								completeModalVisible
							}
							onCancel={() => {
								setCompleteModalVisible(
									false,
								);
								setCompletingInspection(
									null,
								);
								completeForm.resetFields();
							}}
							footer={null}
							width={
								isMobile
									? "95%"
									: 420
							}
							zIndex={1002}
							centered={isMobile}
							destroyOnClose>
							<Form
								form={
									completeForm
								}
								layout="vertical"
								onFinish={
									handleCompleteInspectionSubmit
								}>
								<Form.Item
									name="completed_date"
									label="검사 완료 날짜"
									rules={[
										{
											required: true,
											message: "검사 완료 날짜를 선택하세요.",
										},
									]}>
									<DatePicker
										inputReadOnly={isMobile}
										style={{
											width: "100%",
										}}
										format="YYYY-MM-DD"
									/>
								</Form.Item>
								<Form.Item>
									<Button
										type="primary"
										htmlType="submit"
										loading={
											loading
										}
										block>
										완료
										처리
									</Button>
								</Form.Item>
							</Form>
						</Modal>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ViewRentalCarSchedule;
